import { useState, useEffect, useRef } from 'react';
import { Menu, X, LogIn, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { trackOutbound } from '@/lib/analytics';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { LocalizedLink } from '@/components/LocalizedLink';
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from '@/components/ui/navigation-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AnimatePresence, motion } from 'framer-motion';

// Logo served from Lovable CDN (immutable 1-year cache)
import logoAsset from '../../../public/images/logo.webp.asset.json';
const logo = logoAsset.url;

export function Header() {
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-16 items-center justify-between">
          {/* Logo */}
          <LocalizedLink routeKey="home" className="flex-shrink-0 flex items-center">
            <img src={logo} alt="i-Rent.net" className="h-8 w-auto" width={97} height={32} fetchPriority="high" decoding="async" />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-0 xl:space-x-1 flex-1 justify-center">
            <NavigationMenu>
              <NavigationMenuList>
                {/* Product Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">
                    {t('nav.product')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="productPms" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.pms')}</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              {t('pillars.pms.description')}
                            </p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="productChannelManager" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.channelManager')}</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              {t('pillars.channel.description')}
                            </p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="productWebsiteBuilder" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.websiteBuilder')}</div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              {t('pillars.website.description')}
                            </p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <a
                            href="https://www.magichomephotos.com"
                            onClick={() => trackOutbound('https://www.magichomephotos.com', 'header_products_menu')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                          >
                            <div className="flex items-center gap-1 text-sm font-medium leading-none">
                              {t('nav.magicHomePhotos')}
                              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
                            </div>
                            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                              {t('nav.magicHomePhotos.desc')}
                            </p>
                          </a>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* What is i-Rent.net? Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">
                    {t('nav.whatIsIrent')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-3 p-4">
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="briefDescription" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.briefDescription')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.briefDescription.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="features" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.features')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.features.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="vacationRentalPms" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.holidayRentalPms')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.holidayRentalPms.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="ai" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.ai.menu')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.ai.menu.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* For Whom Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">
                    {t('nav.forWhom')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-3 p-4">
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="forWhomSmallBusiness" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.smallBusiness')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.smallBusiness.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="forWhomMediumLarge" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.mediumLarge')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.mediumLarge.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <LocalizedLink routeKey="integrations" className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                    {t('nav.integrations')}
                  </LocalizedLink>
                </NavigationMenuItem>

                {/* Pricing Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">
                    {t('nav.pricing')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-3 p-4">
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="pricing" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.pricing')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('pricing.nav.chooseYourPlan')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <LocalizedLink routeKey="compare" className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none">{t('nav.compare')}</div>
                            <p className="text-sm leading-snug text-muted-foreground">{t('nav.compare.desc')}</p>
                          </LocalizedLink>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <LocalizedLink routeKey="blog" className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                    {t('nav.resources')}
                  </LocalizedLink>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <LocalizedLink routeKey="about" className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground">
                    {t('nav.about')}
                  </LocalizedLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>


          {/* Right side: Language + CTA (desktop) + hamburger (mobile) */}
          <div className="flex items-center space-x-2 lg:space-x-3">
            {/* Login link with tooltip - always visible */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a href="https://www.rentalbookingsystem.com" target="_blank" rel="noopener noreferrer nofollow" aria-label={t('nav.loginTooltip')} className="text-muted-foreground hover:text-foreground transition-colors p-2 -m-1 inline-flex items-center justify-center">
                    <LogIn size={18} />
                  </a>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('nav.loginTooltip')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Language switcher - always visible */}
            <LanguageSwitcher />
            
            {/* CTA button - desktop only */}
            <Button asChild className="hidden lg:inline-flex">
              <LocalizedLink routeKey="contact">{t('nav.requestDemo')}</LocalizedLink>
            </Button>

            {/* Mobile menu button */}
            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation - outside header to avoid stacking context from backdrop-blur */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto border-t bg-background lg:hidden"
          >
            <nav className="container py-4 space-y-6">
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary/80 mb-2">{t('nav.product')}</p>
                <LocalizedLink routeKey="productPms" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.pms')}</LocalizedLink>
                <LocalizedLink routeKey="productChannelManager" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.channelManager')}</LocalizedLink>
                <LocalizedLink routeKey="productWebsiteBuilder" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.websiteBuilder')}</LocalizedLink>
                <a href="https://www.magichomephotos.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => { trackOutbound('https://www.magichomephotos.com', 'mobile_products_menu'); setMobileMenuOpen(false); }}>{t('nav.magicHomePhotos')}<ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /></a>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary/80 mb-2">{t('nav.whatIsIrent')}</p>
                <LocalizedLink routeKey="briefDescription" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.briefDescription')}</LocalizedLink>
                <LocalizedLink routeKey="features" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.features')}</LocalizedLink>
                <LocalizedLink routeKey="vacationRentalPms" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.holidayRentalPms')}</LocalizedLink>
                <LocalizedLink routeKey="ai" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.ai.menu')}</LocalizedLink>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary/80 mb-2">{t('nav.forWhom')}</p>
                <LocalizedLink routeKey="forWhomSmallBusiness" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.smallBusiness')}</LocalizedLink>
                <LocalizedLink routeKey="forWhomMediumLarge" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.mediumLarge')}</LocalizedLink>
              </div>
              <LocalizedLink routeKey="integrations" className="block py-2.5 text-base font-semibold text-foreground hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.integrations')}</LocalizedLink>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary/80 mb-2">{t('nav.pricing')}</p>
                <LocalizedLink routeKey="pricing" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.pricing')}</LocalizedLink>
                <LocalizedLink routeKey="compare" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.compare')}</LocalizedLink>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-primary/80 mb-2">{t('nav.resources')}</p>
                <LocalizedLink routeKey="blog" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>Blog</LocalizedLink>
                <LocalizedLink routeKey="faq" className="block py-2.5 pl-3 text-base font-medium text-foreground border-l-2 border-border hover:border-primary hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>FAQ</LocalizedLink>
              </div>
              <LocalizedLink routeKey="about" className="block py-2.5 text-base font-semibold text-foreground hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.about')}</LocalizedLink>
              <LocalizedLink routeKey="contact" className="block py-2.5 text-base font-semibold text-foreground hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>{t('nav.contact')}</LocalizedLink>
              <div className="pt-4 border-t">
                <Button asChild className="w-full">
                  <LocalizedLink routeKey="contact" onClick={() => setMobileMenuOpen(false)}>{t('nav.requestDemo')}</LocalizedLink>
                </Button>
              </div>
            </nav>

          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
